import Layout from '../components/Layout.js';
import './Experience.css';

function Experience() {

    const data = [
    {
        start: "May 2026",
        end: "Present",
        jobTitle: "Freelance Software Engineer",
        company: "Independent Contractor",
        summary: "Designed, built, and deployed production web applications for clients.",
        description: "Designed, built, and deployed production web applications with custom frontend development.\n\n◦ Integrated third-party analytics systems to track user engagement and site performance.\n◦ Implemented on-page SEO improvements including metadata optimization, semantic HTML structure, and image alt text to improve search visibility.\n◦ Managed ongoing updates, deployment, and maintenance based on client requirements and feedback.",
        links: [],
        tags: ["Web Development", "Full-Stack Development", "HTML/CSS", "SEO", "Deployment"],
    },
    {
        start: "Jun 2025",
        end: "Present",
        jobTitle: "Lead Software Developer",
        company: "Waffle Works Games",
        summary: "Lead technical developer across multiple interactive narrative projects in Godot and Unreal Engine.",
        description: "Lead technical developer across multiple interactive narrative projects, including a Godot-based 2D game and an Unreal Engine training simulation in development for an Epic MegaGrant proposal.\n\n◦ Architected and implemented core gameplay systems, including dialogue engines, state-driven narrative logic, and reusable engine frameworks in both Godot and Unreal Engine.\n◦ Served as technical lead and primary engineering consultant for clients, translating creative and educational requirements into feasible technical designs and production milestones.\n◦ Collaborated with a team of 5 developers, overseeing task delegation, system integration, and code architecture decisions.\n◦ Contributed to early-stage design and feasibility analysis for an Unreal Engine-based social work training simulation aimed at experiential learning and scenario-based education.",
        links: [],
        tags: ["Godot", "Unreal Engine", "Software Architecture", "Systems Design", "Game Development"],
    },
    {
        start: "Jan 2025",
        end: "May 2025",
        jobTitle: "Undergraduate Research Assistant II",
        company: "NSF REU",
        summary: "Analyzed complexity of computational voting problems and contributed to REU research.",
        description: "Conducted ongoing research into electoral control via candidate and voter partitioning, focusing on developing more natural and realistic models to address gaps in the COMSOC literature.\n\n◦ Led a group of 3 undergraduates through the process of creating formal critiques and technical reports, as well as the academic research process.\n◦ Delivered a research talk summarizing key findings and collaborated on preparing a technical report for submission to a peer-reviewed journal.\n\nAdvised by Dr. Lane Hemaspaandra",
        links: ["https://arxiv.org/abs/2505.05658"],
        tags: ["Problem Solving", "Research", "Algorithm Analysis", "Computational Complexity", "LaTeX"],
    },
    {
        start: "Sept 2023",
        end: "Jun 2024",
        jobTitle: "Undergraduate Research Assistant",
        company: "NSF REU",
        summary: "Developed polynomial-time algorithms and NP-completeness proofs for electoral control models.",
        description: "◦ Formalized 11 polynomial-time algorithms and 3 NP-completeness proofs, expanding research into a more natural representation of modeling electoral control by partitioning with fixed set size limitations.\n◦ Analyzed the algorithmic accuracy and Big-Oh complexity of 2 papers that claimed to resolve the P vs NP problem, resulting in 2 critiques uploaded to arXiv.org.\n◦ Collaborated with other undergraduates on constructing NP-completeness classifications and participated in weekly discussions of COMSOC papers.\n◦ Served as a conference referee for 2 papers at the 49th International Symposium on Mathematical Foundations of Computer Science (MFCS 2024).\n\nAdvised by Dr. Lane Hemaspaandra",
        links: ["https://arxiv.org/abs/2312.04395", "https://arxiv.org/abs/2404.00006"],
        tags: ["Problem Solving", "Research", "Algorithm Design", "Computational Complexity", "LaTeX"],
    },
    {
        start: "May 2023",
        end: "Dec 2023",
        jobTitle: "Undergraduate Researcher",
        company: "University of Rochester",
        summary: "Created novel AI classifiers that outperform humans at detecting AI-generated images.",
        description: "◦ Designed and implemented 5 deep-learning classifiers to detect AI-generated images, including 3 fine-tuned Convolutional Neural Networks (CNNs) and 2 feature extractors based on Bayer array artifacts.\n◦ Achieved above-human performance (73%–86% accuracy) on a composite dataset of 1,460 genuine and 1,340 AI-generated images.\n◦ Primary author of a paper published at the IEEE International Conference on Big Data 2023.\n\nAdvised by Dr. Jiebo Luo",
        links: ["https://ieeexplore.ieee.org/document/10386096", "https://github.com/notred27/CFA-Interpolation-Classifiers-on-Identifying-AIGC"],
        tags: ["Deep Learning", "Computer Vision", "Academic Writing", "Research", "Python"],
    },
    {
        start: "Jan 2023",
        end: "May 2023",
        jobTitle: "Undergraduate Teaching Assistant",
        company: "University of Rochester",
        summary: "Held office hours and supported 20+ students implementing data structures in Java.",
        description: "◦ Held office hours twice a week to support 20+ students with Java implementations of key data structures and algorithms (linked lists, trees, heaps, and graphs).\n◦ Provided individual assistance for topics such as Dijkstra's algorithm and Huffman coding, and showed demos during lab sessions to reinforce concepts with hands-on examples.\n◦ Collaborated with the course instructor to develop grading rubrics, manage grade records, and provide feedback on coding exercises and projects.\n\nU of R CSC 172 — Data Structures and Algorithms",
        links: [],
        tags: ["Teaching", "Java", "Data Structures", "Organization"],
    },
    {
        start: "Sept 2022",
        end: "Dec 2022",
        jobTitle: "Undergraduate Workshop Leader",
        company: "University of Rochester",
        summary: "Taught 17 students core data structures and improved engagement using recall-based strategies.",
        description: "◦ Taught 17 undergraduates both theoretical concepts of data structures and their practical implementations in Java.\n◦ Developed and led 11 lesson plans on core data structures, including linked-lists, stacks, queues, trees, heaps, and graphs.\n◦ Investigated the effect of recall questions on students' academic performance, resulting in over 64.7% of students reporting higher engagement during lessons.\n\nU of R CSC 172 — Data Structures and Algorithms",
        links: [],
        tags: ["Teaching", "Communication", "Java", "Data Structures", "Organization"],
    },
];

    // Helper: extract year from "May 2023" → "2023"
    function getGroupingYear(item) {
        if (item.end === "Present") return "Present";  // ← add this
        const extractYear = (str) => {
            const match = str.match(/\b\d{4}\b/);
            return match ? match[0] : null;
        };
        const endYear = extractYear(item.end);
        const startYear = extractYear(item.start);
        return endYear ? endYear : startYear;
    }

    function groupByYear(data) {
        return data.reduce((acc, item) => {
            const year = getGroupingYear(item);
            if (!acc[year]) acc[year] = [];
            acc[year].push(item);
            return acc;
        }, {});
    }

    const groupedData = groupByYear(data);

    function TimelineEntry({ data }) {
        return (
            <div className="timeline-entry">
                <div className="timeline-content">

                    <span className="timeline-date">
                        {data.start} — {data.end}
                    </span>

                    <h3 className="timeline-title">
                        {data.jobTitle} •{' '}
                        <span className="company">{data.company}</span>
                    </h3>

                    {/* Only render description block if there's actual text */}
                    {data.description && (
                        <p className="timeline-description" style={{ whiteSpace: 'pre-line' }}>
                            {data.description}
                        </p>
                    )}

                    {data.links.length > 0 && (
                        <div className="timeline-links">
                            {data.links.map((link, i) => (
                                <a
                                    key={i}
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="timeline-link"
                                >
                                    <ion-icon name="link-outline" style={{ fontSize: '14px' }} />
                                    {link.replace(/^https?:\/\//, '')}
                                </a>
                            ))}
                        </div>
                    )}

                    {data.tags.length > 0 && (
                        <div className="timeline-tags">
                            {data.tags.map((tag, i) => (
                                <span key={i} className="timeline-tag">
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}

                </div>
            </div>
        );
    }


    return (
        <Layout pageIndex={2}>
            <div className="exp-hero page-hero noise">
                <div className="exp-page-header">
                    <p className="section-eyebrow">Where I've worked</p>
                    <h1 className="exp-page-title">Experience</h1>
                    <p className="exp-page-sub">{data.length} roles across research, freelance work, and game development.</p>
                </div>
            </div>
            <div className="timeline-container">
                {Object.entries(groupedData)
                    .sort((a, b) => {
                        if (a[0] === "Present") return -1;
                        if (b[0] === "Present") return 1;
                        return b[0] - a[0];
                    })
                    .map(([year, jobs]) => (
                        <div key={year} className="year-group">
                            <div className="year-heading-wrapper">
                                <h2 className="year-heading">{year}</h2>
                            </div>
                            {jobs.map((job, i) => (
                                <TimelineEntry key={i} data={job} />
                            ))}
                        </div>
                    ))}
            </div>
        </Layout>
    );
}

export default Experience;